import * as React from 'react'
import { DatabaseZap, FolderOpen, BookOpen, RefreshCw, Loader2, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSetAtom } from 'jotai'
import { SourceAvatar } from '@/components/ui/source-avatar'
import { deriveConnectionStatus } from '@/components/ui/source-status-indicator'
import { EntityPanel } from '@/components/ui/entity-panel'
import type { EntityListGroup } from '@/components/ui/entity-list'
import { EntityListBadge } from '@/components/ui/entity-list-badge'
import { EntityListEmptyScreen } from '@/components/ui/entity-list-empty'
import { sourceSelection } from '@/hooks/useEntitySelection'
import { SourceMenu } from './SourceMenu'
import { EditPopover, getEditConfig, type EditContextKey } from '@/components/ui/EditPopover'
import { CreateKnowledgeCollectionDialog } from './CreateKnowledgeCollectionDialog'
import { toast } from 'sonner'
import { isIPad } from '@/lib/platform'
import { pendingEditCollectionSlugAtom } from '@/atoms/sources'
import type { LoadedSource, SourceConnectionStatus, SourceFilter } from '@/shared/types'

const SOURCE_TYPE_CONFIG: Record<string, { label: string; colorClass: string }> = {
  mcp: { label: 'MCP', colorClass: 'bg-accent/10 text-accent' },
  api: { label: 'API', colorClass: 'bg-success/10 text-success' },
  local: { label: 'Local', colorClass: 'bg-info/10 text-info' },
  knowledge: { label: 'Knowledge', colorClass: 'bg-purple-500/10 text-purple-500' },
}

const SOURCE_STATUS_CONFIG: Record<string, { label: string; colorClass: string; tooltip: string }> = {
  connected: { label: 'Online', colorClass: 'bg-success/10 text-success', tooltip: 'Connected and ready to use' },
  needs_auth: { label: 'Inactive', colorClass: 'bg-foreground/5 text-foreground/40', tooltip: 'Authentication required — tap to set up' },
  failed: { label: 'Inactive', colorClass: 'bg-foreground/5 text-foreground/40', tooltip: 'Something went wrong — please reauthenticate' },
  untested: { label: 'Not connected', colorClass: 'bg-warning/10 text-warning', tooltip: 'This connector hasn\'t been used yet' },
  local_disabled: { label: 'Disabled', colorClass: 'bg-foreground/10 text-foreground/50', tooltip: 'Disabled — enable in connector settings' },
}

const SOURCE_TYPE_FILTER_LABELS: Record<string, string> = {
  api: 'API',
  mcp: 'MCP',
  local: 'local folder',
  knowledge: 'knowledge',
}

interface KnowledgeCollection {
  id: string
  name: string
  description: string | null
  icon: string | null
}

function KnowledgeEmptyState({ workspaceId }: { workspaceId: string }) {
  const [collections, setCollections] = React.useState<KnowledgeCollection[]>([])
  const [loading, setLoading] = React.useState(true)
  const [connecting, setConnecting] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = React.useState(false)

  const fetchCollections = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await window.electronAPI.knowledgeListCollections()
      setCollections(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collections')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { fetchCollections() }, [fetchCollections])

  // Auto-connect all available collections on first load
  const autoConnectAttempted = React.useRef(false)
  React.useEffect(() => {
    if (loading || error || collections.length === 0 || autoConnectAttempted.current) return
    autoConnectAttempted.current = true
    const autoConnect = async () => {
      for (const collection of collections) {
        setConnecting(collection.id)
        try {
          const source = await window.electronAPI.createSource(workspaceId, {
            name: collection.name,
            provider: 'enterprise-knowledge',
            type: 'knowledge',
            icon: collection.icon ?? '📘',
            tagline: collection.description ?? undefined,
            knowledge: { collectionId: collection.id },
          })
          await window.electronAPI.knowledgePull(workspaceId, source.slug)
          toast.success(`Connected to "${collection.name}"`)
        } catch (err) {
          toast.error('Failed to connect', {
            description: err instanceof Error ? err.message : 'Unknown error',
          })
        } finally {
          setConnecting(null)
        }
      }
    }
    autoConnect()
  }, [loading, error, collections, workspaceId])

  const handleConnect = React.useCallback(async (collection: KnowledgeCollection) => {
    setConnecting(collection.id)
    try {
      const source = await window.electronAPI.createSource(workspaceId, {
        name: collection.name,
        provider: 'enterprise-knowledge',
        type: 'knowledge',
        icon: collection.icon ?? '📘',
        tagline: collection.description ?? undefined,
        knowledge: { collectionId: collection.id },
      })
      // Trigger initial pull
      await window.electronAPI.knowledgePull(workspaceId, source.slug)
      toast.success(`Connected to "${collection.name}"`)
    } catch (err) {
      toast.error('Failed to connect', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setConnecting(null)
    }
  }, [workspaceId])

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-3 rounded-lg p-6 pb-[20%] text-center text-balance">
      <div className="flex max-w-sm flex-col items-center gap-2 text-center">
        <div className="text-muted-foreground flex shrink-0 items-center justify-center mb-2 [&_svg]:size-10 [&_svg]:stroke-[1.5]">
          <BookOpen />
        </div>
        <div className="text-sm font-medium tracking-tight">No knowledge bases connected.</div>
        <div className="text-muted-foreground text-xs/snug">
          Connect a knowledge base to give your agent access to shared documents and guidelines.
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading collections…
        </div>
      )}
      {error && (
        <div className="flex flex-col items-center gap-2 mt-3">
          <span className="text-xs text-destructive">{error}</span>
          <button
            onClick={fetchCollections}
            className="inline-flex items-center gap-1.5 h-7 px-3 text-xs font-medium rounded-[8px] bg-background shadow-minimal hover:bg-foreground/[0.03] transition-colors"
          >
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        </div>
      )}
      {!loading && !error && collections.length === 0 && (
        <button
          onClick={() => setShowCreateDialog(true)}
          className="inline-flex items-center gap-1.5 h-7 px-3 text-xs font-medium rounded-[8px] bg-background shadow-minimal hover:bg-foreground/[0.03] transition-colors mt-3"
        >
          <Plus className="h-3 w-3" />
          New Collection
        </button>
      )}
      {!loading && !error && collections.length > 0 && (
        <div className="flex flex-col gap-1.5 w-full max-w-[260px] mt-3">
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center justify-center gap-1.5 w-full h-8 px-3 text-xs font-medium rounded-[8px] bg-background shadow-minimal hover:bg-foreground/[0.03] transition-colors mb-1"
          >
            <Plus className="h-3 w-3" />
            New Collection
          </button>
          <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">Available Collections</div>
          {collections.map((col) => (
            <button
              key={col.id}
              onClick={() => handleConnect(col)}
              disabled={connecting !== null}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-left rounded-[8px] bg-background shadow-minimal hover:bg-foreground/[0.03] transition-colors disabled:opacity-50"
            >
              <span className="text-base shrink-0">{col.icon ?? '📘'}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{col.name}</div>
                {col.description && (
                  <div className="text-[11px] text-muted-foreground truncate">{col.description}</div>
                )}
              </div>
              {connecting === col.id && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />}
            </button>
          ))}
        </div>
      )}

      <CreateKnowledgeCollectionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        workspaceId={workspaceId}
        onCreated={() => {
          fetchCollections()
        }}
      />
    </div>
  )
}

export interface SourcesListPanelProps {
  sources: LoadedSource[]
  sourceFilter?: SourceFilter | null
  workspaceId?: string
  workspaceRootPath?: string
  onDeleteSource: (sourceSlug: string) => void
  onSourceClick: (source: LoadedSource) => void
  selectedSourceSlug?: string | null
  localMcpEnabled?: boolean
  className?: string
}

export function SourcesListPanel({
  sources,
  sourceFilter,
  workspaceId,
  workspaceRootPath,
  onDeleteSource,
  onSourceClick,
  selectedSourceSlug,
  localMcpEnabled = true,
  className,
}: SourcesListPanelProps) {
  const setPendingEditCollectionSlug = useSetAtom(pendingEditCollectionSlugAtom)
  const isKnowledgeFilter = sourceFilter?.kind === 'type' && sourceFilter.sourceType === 'knowledge'

  const filteredSources = React.useMemo(() => {
    if (!sourceFilter) {
      // No filter = "Connectors" view: only show api + mcp sources
      return sources.filter(s => s.config.type === 'api' || s.config.type === 'mcp')
    }
    return sources.filter(s => s.config.type === sourceFilter.sourceType)
  }, [sources, sourceFilter])

  const sourceGroups = React.useMemo(() => {
    if (isKnowledgeFilter) return null

    const builtIn = filteredSources
      .filter(s => s.config.isDefault === true)
      .sort((a, b) => a.config.name.localeCompare(b.config.name))
    const custom = filteredSources
      .filter(s => s.config.isDefault !== true)
      .sort((a, b) => a.config.name.localeCompare(b.config.name))

    const groups: EntityListGroup<LoadedSource>[] = []
    if (builtIn.length > 0) groups.push({ key: 'built-in', label: 'Built-in', items: builtIn })
    if (custom.length > 0) groups.push({ key: 'custom', label: 'Custom', items: custom })

    // Suppress header when only one group exists
    if (groups.length <= 1) return null

    return groups
  }, [filteredSources, isKnowledgeFilter])

  const sortedFlatSources = React.useMemo(() => {
    if (sourceGroups) return undefined
    return [...filteredSources].sort((a, b) => a.config.name.localeCompare(b.config.name))
  }, [filteredSources, sourceGroups])

  const emptyMessage = React.useMemo(() => {
    if (sourceFilter?.kind === 'type') {
      return `No ${SOURCE_TYPE_FILTER_LABELS[sourceFilter.sourceType] ?? sourceFilter.sourceType} connectors configured.`
    }
    return 'No connectors configured.'
  }, [sourceFilter])

  const showCloudHint = isKnowledgeFilter && filteredSources.length > 0

  return (
    <div className={cn('flex flex-col flex-1 min-h-0', className)}>
    <EntityPanel<LoadedSource>
      items={sortedFlatSources}
      groups={sourceGroups ?? undefined}
      getId={(s) => s.config.slug}
      selection={sourceSelection}
      selectedId={selectedSourceSlug}
      onItemClick={onSourceClick}
      className="flex-1 min-h-0"
      emptyState={
        isKnowledgeFilter && workspaceId ? (
          <KnowledgeEmptyState workspaceId={workspaceId} />
        ) : (
          <EntityListEmptyScreen
            icon={sourceFilter?.kind === 'type' && sourceFilter.sourceType === 'local' ? <FolderOpen /> : <DatabaseZap />}
            title={emptyMessage}
            description={sourceFilter?.kind === 'type' && sourceFilter.sourceType === 'local'
              ? "Add folders from your computer so your agent can read and work with your files — documents, spreadsheets, code, and more."
              : "Connectors link your agent to external services, APIs, and data sources."}
            docKey="sources"
          >
            {workspaceRootPath && (
              <EditPopover
                align="center"
                trigger={
                  <button className="inline-flex items-center h-7 px-3 text-xs font-medium rounded-[8px] bg-background shadow-minimal hover:bg-foreground/[0.03] transition-colors">
                    Add Connector
                  </button>
                }
                {...getEditConfig(
                  sourceFilter?.kind === 'type' ? `add-source-${sourceFilter.sourceType}` as EditContextKey : 'add-source',
                  workspaceRootPath
                )}
              />
            )}
          </EntityListEmptyScreen>
        )
      }
      mapItem={(source) => {
        const connectionStatus = deriveConnectionStatus(source, localMcpEnabled)
        const typeConfig = SOURCE_TYPE_CONFIG[source.config.type]
        const statusConfig = SOURCE_STATUS_CONFIG[connectionStatus]
        const subtitle = source.config.tagline || (source.config.type === 'knowledge' ? '' : source.config.provider || '')
        return {
          icon: <SourceAvatar source={source} size="sm" />,
          title: source.config.name,
          badges: (
            <>
              {typeConfig && source.config.type !== 'mcp' && source.config.type !== 'api' && (
                <EntityListBadge colorClass={typeConfig.colorClass}>{typeConfig.label}</EntityListBadge>
              )}
              {statusConfig && (
                <EntityListBadge colorClass={statusConfig.colorClass} tooltip={statusConfig.tooltip} className="cursor-default">
                  {statusConfig.label}
                </EntityListBadge>
              )}
              {subtitle && <span className="truncate">{subtitle}</span>}
            </>
          ),
          menu: (
            <SourceMenu
              sourceSlug={source.config.slug}
              sourceName={source.config.name}
              isDefault={source.config.isDefault}
              isProtectedKnowledge={source.config.type === 'knowledge' && source.config.knowledge?.isProtected === true}
              onEditCollection={source.config.type === 'knowledge' ? () => {
                setPendingEditCollectionSlug(source.config.slug)
                onSourceClick(source)
              } : undefined}
              onOpenInNewWindow={isIPad ? () => {} : () => window.electronAPI.openUrl(`originrelay://sources/source/${source.config.slug}?window=focused`)}
              onShowInFinder={isIPad ? () => {} : () => window.electronAPI.showInFolder(source.folderPath)}
              onDelete={() => onDeleteSource(source.config.slug)}
            />
          ),
        }
      }}
    />
    {showCloudHint && (
      <div className="px-4 pt-2.5 pb-3 text-xs text-muted-foreground/70">
        Knowledge collections are automatically synced to the cloud and made available to all team members.
      </div>
    )}
    </div>
  )
}
